import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
  useProjectShares,
  useCreateShare,
  useUpdateShare,
  useDeleteShare,
  useRegenerateShareToken,
} from '../hooks/api';
import {
  Share2, Copy, Trash2, RefreshCw, Lock, Unlock, X, Pencil,
  Calendar, Link as LinkIcon, Power, ExternalLink, Loader2, Check,
} from 'lucide-react';
import { format } from 'date-fns';

/** Public share links always expose full project data; kept for API compatibility. */
const FULL_VISIBILITY = {
  show_board: true,
  show_analytics: true,
  show_team: true,
  show_time_tracking: true,
  show_task_details: true,
  show_priority: true,
  show_due_dates: true,
};

const STAKEHOLDER_INCLUDES = [
  'Project overview & progress',
  'All tasks (board + list)',
  'Sprints & goals',
  'Team workload',
  'Time tracking & hours',
  'Recent activity',
];

const emptyForm = () => ({ title: '', password: '', expires_at: '' });

export default function ShareSettingsModal({ open, onClose, projectId, projectName }) {
  const { data: shares = [], isLoading, isFetching } = useProjectShares(projectId);
  const createShare = useCreateShare();
  const updateShare = useUpdateShare();
  const deleteShare = useDeleteShare();
  const regenerateToken = useRegenerateShareToken();

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  /** { shareId: string | null, action: string } while an async action runs */
  const [busy, setBusy] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const shareBaseUrl = typeof window !== 'undefined' ? `${window.location.origin}/share` : '/share';
  const shareUrl = (token) => `${shareBaseUrl}/${token}`;

  const isCreating = busy?.action === 'create' || createShare.isPending;
  const isSavingEdit = (shareId) => busy?.action === 'save' && busy?.shareId === shareId;
  const isDeleting = (shareId) => busy?.action === 'delete' && busy?.shareId === shareId;
  const isRegenerating = (shareId) => busy?.action === 'regenerate' && busy?.shareId === shareId;
  const isToggling = (shareId) => busy?.action === 'toggle' && busy?.shareId === shareId;
  const isRowBusy = (shareId) => isSavingEdit(shareId) || isDeleting(shareId) || isRegenerating(shareId) || isToggling(shareId);

  const copyLink = async (shareId, token) => {
    try {
      await navigator.clipboard.writeText(shareUrl(token));
      setCopiedId(shareId);
      toast.success('Link copied');
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error('Could not copy link');
    }
  };

  const openPreview = (token) => {
    window.open(shareUrl(token), '_blank', 'noopener,noreferrer');
  };

  const handleCreate = async () => {
    setBusy({ shareId: null, action: 'create' });
    try {
      await createShare.mutateAsync({
        project_id: projectId,
        title: form.title.trim() || undefined,
        password: form.password.trim() || undefined,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : undefined,
        visibility_settings: FULL_VISIBILITY,
      });
      toast.success('Share link created');
      setCreating(false);
      setForm(emptyForm());
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create share link');
    } finally {
      setBusy(null);
    }
  };

  const handleUpdate = async (shareId) => {
    if (!editForm) return;
    setBusy({ shareId, action: 'save' });
    try {
      const data = {
        title: editForm.title || null,
        expires_at: editForm.expires_at ? new Date(editForm.expires_at).toISOString() : null,
        visibility_settings: FULL_VISIBILITY,
      };
      if (editForm.password !== undefined && editForm.password !== '') {
        data.password = editForm.password;
      }
      await updateShare.mutateAsync({ shareId, data });
      toast.success('Share link updated');
      setEditingId(null);
      setEditForm(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update');
    } finally {
      setBusy(null);
    }
  };

  const handleDelete = async (shareId) => {
    if (!window.confirm('Revoke this share link? Anyone with the link will no longer be able to view.')) return;
    setBusy({ shareId, action: 'delete' });
    try {
      await deleteShare.mutateAsync(shareId);
      toast.success('Share link revoked');
      if (editingId === shareId) {
        setEditingId(null);
        setEditForm(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to revoke');
    } finally {
      setBusy(null);
    }
  };

  const handleToggleActive = async (share) => {
    setBusy({ shareId: share.id, action: 'toggle' });
    try {
      await updateShare.mutateAsync({ shareId: share.id, data: { is_active: !share.is_active } });
      toast.success(share.is_active ? 'Link deactivated' : 'Link activated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update');
    } finally {
      setBusy(null);
    }
  };

  const handleRegenerate = async (shareId) => {
    if (!window.confirm('Generate a new link? The current link will stop working.')) return;
    setBusy({ shareId, action: 'regenerate' });
    try {
      const res = await regenerateToken.mutateAsync(shareId);
      await copyLink(shareId, res.token);
      toast.success('New link generated and copied');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to regenerate');
    } finally {
      setBusy(null);
    }
  };

  const startEdit = (share) => {
    if (busy) return;
    setEditingId(share.id);
    setEditForm({
      title: share.title || '',
      password: '',
      expires_at: share.expires_at ? format(new Date(share.expires_at), "yyyy-MM-dd'T'HH:mm") : '',
    });
  };

  const cancelCreate = () => {
    if (isCreating) return;
    setCreating(false);
    setForm(emptyForm());
  };

  const cancelEdit = () => {
    if (busy?.action === 'save') return;
    setEditingId(null);
    setEditForm(null);
  };

  if (!open) return null;

  const listLoading = isLoading || (isFetching && shares.length === 0);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={busy ? undefined : onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl border border-secondary-200 max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-secondary-200">
          <div>
            <h2 className="text-lg font-semibold text-secondary-900">Share with stakeholders</h2>
            {projectName && <p className="text-sm text-secondary-500 mt-0.5">{projectName}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={!!busy}
            className="p-1.5 rounded-lg text-secondary-400 hover:text-secondary-700 hover:bg-secondary-100 disabled:opacity-40 disabled:pointer-events-none"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1 space-y-5">
          <StakeholderInfo />

          {!creating ? (
            <button
              type="button"
              onClick={() => setCreating(true)}
              disabled={!!busy}
              className="btn-primary flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <LinkIcon className="h-4 w-4" />
              Create share link
            </button>
          ) : (
            <div className="rounded-xl border border-secondary-200 bg-secondary-50 p-4">
              <h3 className="text-sm font-semibold text-secondary-900 mb-3">New share link</h3>
              <div className="space-y-3">
                <Field label="Custom title (optional)">
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                    placeholder="e.g. Q1 Progress Report"
                    className="input-field w-full"
                    disabled={isCreating}
                  />
                </Field>
                <Field label="Password (optional)">
                  <input
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                    placeholder="Leave empty for open access"
                    className="input-field w-full"
                    disabled={isCreating}
                  />
                </Field>
                <Field label="Expires at (optional)">
                  <input
                    type="datetime-local"
                    value={form.expires_at}
                    onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))}
                    className="input-field w-full"
                    disabled={isCreating}
                  />
                </Field>
                <div className="flex gap-2 pt-1">
                  <button
                    type="button"
                    onClick={handleCreate}
                    className="btn-primary inline-flex items-center justify-center gap-2 min-w-[8.5rem]"
                    disabled={isCreating}
                  >
                    {isCreating ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Creating link…
                      </>
                    ) : (
                      'Create link'
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={cancelCreate}
                    className="btn-secondary"
                    disabled={isCreating}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          <div>
            <h3 className="text-sm font-semibold text-secondary-900 mb-3">Existing links</h3>
            {listLoading ? (
              <div className="flex items-center gap-2 text-secondary-500 text-sm py-4">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading links…
              </div>
            ) : shares.length === 0 ? (
              <p className="text-secondary-500 text-sm">No share links yet.</p>
            ) : (
              <ul className="space-y-3">
                {shares.map((share) => (
                  <li
                    key={share.id}
                    className={`rounded-xl border p-4 transition-opacity ${
                      isRowBusy(share.id) ? 'border-primary-200 bg-primary-50/30 opacity-90' : 'border-secondary-200'
                    }`}
                  >
                    {editingId === share.id && editForm ? (
                      <div className="space-y-3">
                        <Field label="Title">
                          <input
                            type="text"
                            value={editForm.title}
                            onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
                            placeholder="Title"
                            className="input-field w-full text-sm"
                            disabled={isSavingEdit(share.id)}
                          />
                        </Field>
                        <Field label="New password (optional)">
                          <input
                            type="password"
                            value={editForm.password}
                            onChange={(e) => setEditForm((f) => ({ ...f, password: e.target.value }))}
                            placeholder="Leave blank to keep current"
                            className="input-field w-full text-sm"
                            disabled={isSavingEdit(share.id)}
                          />
                        </Field>
                        <Field label="Expires at">
                          <input
                            type="datetime-local"
                            value={editForm.expires_at}
                            onChange={(e) => setEditForm((f) => ({ ...f, expires_at: e.target.value }))}
                            className="input-field w-full text-sm"
                            disabled={isSavingEdit(share.id)}
                          />
                        </Field>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => handleUpdate(share.id)}
                            className="btn-primary text-sm inline-flex items-center justify-center gap-2 min-w-[5.5rem]"
                            disabled={isSavingEdit(share.id)}
                          >
                            {isSavingEdit(share.id) ? (
                              <>
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                Saving…
                              </>
                            ) : (
                              'Save'
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={cancelEdit}
                            className="btn-secondary text-sm"
                            disabled={isSavingEdit(share.id)}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            {share.has_password
                              ? <Lock className="h-4 w-4 text-amber-600 shrink-0" />
                              : <Unlock className="h-4 w-4 text-secondary-400 shrink-0" />}
                            <span className="text-sm font-medium text-secondary-900 truncate">
                              {share.title || 'Untitled share'}
                            </span>
                            {!share.is_active && (
                              <span className="text-xs font-medium text-red-600 bg-red-50 px-1.5 py-0.5 rounded">Inactive</span>
                            )}
                            {isToggling(share.id) && (
                              <span className="text-xs text-primary-600 flex items-center gap-1">
                                <Loader2 className="h-3 w-3 animate-spin" />
                                Updating…
                              </span>
                            )}
                            {isDeleting(share.id) && (
                              <span className="text-xs text-red-600 flex items-center gap-1">
                                <Loader2 className="h-3 w-3 animate-spin" />
                                Revoking…
                              </span>
                            )}
                            {isRegenerating(share.id) && (
                              <span className="text-xs text-primary-600 flex items-center gap-1">
                                <Loader2 className="h-3 w-3 animate-spin" />
                                Regenerating…
                              </span>
                            )}
                          </div>
                          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-secondary-500">
                            <span>{share.access_count} view{share.access_count !== 1 ? 's' : ''}</span>
                            {share.last_accessed_at && (
                              <span>Last viewed {format(new Date(share.last_accessed_at), 'MMM d, yyyy')}</span>
                            )}
                            {share.expires_at && (
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                Expires {format(new Date(share.expires_at), 'MMM d, yyyy')}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-0.5 flex-shrink-0">
                          <IconBtn
                            onClick={() => copyLink(share.id, share.token)}
                            title="Copy link"
                            disabled={isRowBusy(share.id)}
                            className={copiedId === share.id ? 'text-emerald-600 bg-emerald-50' : ''}
                          >
                            {copiedId === share.id ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                          </IconBtn>
                          <IconBtn onClick={() => openPreview(share.token)} title="Open preview" disabled={isRowBusy(share.id)}>
                            <ExternalLink className="h-4 w-4" />
                          </IconBtn>
                          <IconBtn
                            onClick={() => handleRegenerate(share.id)}
                            title="Regenerate link"
                            disabled={isRowBusy(share.id)}
                          >
                            {isRegenerating(share.id)
                              ? <Loader2 className="h-4 w-4 animate-spin" />
                              : <RefreshCw className="h-4 w-4" />}
                          </IconBtn>
                          <IconBtn
                            onClick={() => handleToggleActive(share)}
                            title={share.is_active ? 'Deactivate' : 'Activate'}
                            disabled={isRowBusy(share.id)}
                            className={share.is_active ? 'hover:bg-amber-50 text-amber-600' : 'hover:bg-emerald-50 text-emerald-600'}
                          >
                            {isToggling(share.id)
                              ? <Loader2 className="h-4 w-4 animate-spin" />
                              : <Power className="h-4 w-4" />}
                          </IconBtn>
                          <IconBtn onClick={() => startEdit(share)} title="Edit" disabled={!!busy}>
                            <Pencil className="h-4 w-4" />
                          </IconBtn>
                          <IconBtn
                            onClick={() => handleDelete(share.id)}
                            title="Revoke"
                            disabled={isRowBusy(share.id)}
                            className="hover:bg-red-50 text-red-600"
                          >
                            {isDeleting(share.id)
                              ? <Loader2 className="h-4 w-4 animate-spin" />
                              : <Trash2 className="h-4 w-4" />}
                          </IconBtn>
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StakeholderInfo() {
  return (
    <div className="rounded-xl border border-primary-200 bg-primary-50/60 px-4 py-3">
      <div className="flex items-start gap-2.5">
        <Share2 className="h-4 w-4 text-primary-600 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm font-medium text-secondary-900">Stakeholders see the full project report</p>
          <p className="text-xs text-secondary-600 mt-1 leading-relaxed">
            Share links include all tasks, team workload, sprints, time tracking, and activity — no section is hidden.
          </p>
          <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
            {STAKEHOLDER_INCLUDES.map((item) => (
              <li key={item} className="text-xs text-secondary-600 flex items-center gap-1.5">
                <span className="w-1 h-1 rounded-full bg-primary-500 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-secondary-600 mb-1">{label}</label>
      {children}
    </div>
  );
}

function IconBtn({ children, onClick, title, className = '', disabled = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={disabled}
      className={`p-2 rounded-lg text-secondary-600 hover:bg-secondary-100 transition-colors disabled:opacity-40 disabled:pointer-events-none disabled:cursor-not-allowed ${className}`}
    >
      {children}
    </button>
  );
}
